import { z } from 'zod';
import { isMongoId } from '../../helpers';
import CalendarDay from '../../../utils/calendar-day';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { AllocationRule } from 'further-types/lp/allocations';
import { AllocationSchema, LinkedActionSchema } from '../reallocation-layer';
import { ActionRef } from 'further-types/lp/action';
import { IndividualTransaction } from '../transaction';

const PartnerTransferIndividualTransaction = IndividualTransaction.extend({
  commitmentId: z
    .string()
    .refine(isMongoId({ optional: true }))
    .optional(),
  label: z.string().optional(),
  effectiveCapitalDate: z.instanceof(CalendarDay).optional(),
});

const PartnerTransferActionSchema = z.object({
  actionId: z.string().refine(isMongoId()),
  actionType: z.string(),
  actionRef: z.nativeEnum(ActionRef).optional(),
  counterpartyLabel: z.string(),
  ledgerDate: z.instanceof(CalendarDay),
  totalAmountLcy: z.number(),
  allocationRule: z.nativeEnum(AllocationRule),
  originalAllocations: z.array(AllocationSchema).optional(),
  newAllocations: z.array(AllocationSchema),
  linkedActions: z.array(LinkedActionSchema),
});

export const CreatePartnerTransfer = z.object({
  partnerTransferId: z.string().refine(isMongoId({ optional: true })),
  trasferrorCommitmentId: z
    .string()
    .min(1, { message: 'Please select a commitment' })
    .refine(isMongoId(), { message: 'Invalid commitment ID' }),
  transferDate: z.instanceof(CalendarDay, {
    message: 'Please select a transfer date',
  }),
  currentSection: z.nativeEnum(PartnerTransferSection),
  actions: z
    .array(
      z.object({
        section: z.nativeEnum(PartnerTransferSection),
        actions: z.array(PartnerTransferActionSchema).optional(),
      }),
    )
    .optional(),
  transferees: z
    .array(
      z.object({
        investorLabel: z.string().optional(),
        investorId: z.string().refine(isMongoId()),
        amountLcy: z.number().min(0),
        percentage: z.number().min(0).max(100),
      }),
    )
    .min(1, { message: 'At least one transferee is required' }),
  capitalActionTransactions: z
    .record(z.string(), z.array(PartnerTransferIndividualTransaction))
    .optional(),
  feeTransactions: z
    .record(z.string(), z.array(PartnerTransferIndividualTransaction))
    .optional(),
});

export const ValidatePartnerTransferInputSchema = z.object({
  vehicleId: z.string().refine(isMongoId()),
  commitmentId: z.string().refine(isMongoId()),
  transferDate: z.instanceof(CalendarDay),
  partnerTransferId: z
    .string()
    .refine(isMongoId({ optional: true }))
    .optional(),
});

export const CreatePartnerTransferInputSchema = z.object({
  vehicleId: z.string().refine(isMongoId()),
  transferDate: z.instanceof(CalendarDay),
  transferorCommitmentId: z.string().refine(isMongoId()),
  transferees: z
    .array(
      z.object({
        investorId: z.string().refine(isMongoId()),
        amountLcy: z.number(),
        percentage: z.number(),
      }),
    )
    .min(1),
});

export const UpdatePartnerTransferInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
  transferDate: z.instanceof(CalendarDay),
  transferorCommitmentId: z.string().refine(isMongoId()),
  currentSection: z.nativeEnum(PartnerTransferSection),
  actions: z
    .array(
      z.object({
        section: z.nativeEnum(PartnerTransferSection),
        actions: z.array(PartnerTransferActionSchema).optional(),
      }),
    )
    .optional(),
  transferees: z
    .array(
      z.object({
        investorId: z.string().refine(isMongoId()),
        amountLcy: z.number(),
        percentage: z.number(),
      }),
    )
    .min(1),
  saveActionLinkedCapitalActions: z.boolean().optional(),
  saveActionGeneralCapitalActions: z.boolean().optional(),
  saveActionAdvanceSubscriptions: z.boolean().optional(),
  saveActionFees: z.boolean().optional(),
});

export const GetActionAllocationsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
  partnerTransferSection: z.nativeEnum(PartnerTransferSection),
});

export const ApprovePartnerTransferInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
  capitalActionTransactions: z
    .record(z.string(), z.array(PartnerTransferIndividualTransaction))
    .optional(),
  feeTransactions: z
    .record(z.string(), z.array(PartnerTransferIndividualTransaction))
    .optional(),
});

export const GetByIdInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetLinkedCapitalActionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
});

export const GetLinkedCapitalActionsTransactionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetGeneralCapitalActionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetGeneralCapitalActionsTransactionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetAdvanceSubscriptionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetAdvanceSubscriptionsTransactionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetFeesInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const GetFeeTransactionsInputSchema = z.object({
  partnerTransferId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});
